import Phaser from 'phaser';
import { fetchTop, type LeaderboardEntry, getPlayerName } from '../services/leaderboard';
import { resumeAudio } from '../services/audio';
import { addNeonBackdrop } from '../util/glow';
import { createButton } from '../util/button';
import { drawTrophy, drawMedal } from '../util/glyphs';
import { activePalette, css, lighten } from '../util/palette';

const DPR = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
const FONT = 'Fredoka, system-ui, -apple-system, sans-serif';

// Medal disc + rim colors for the top three. Universal gold/silver/bronze so
// the podium reads the same across every palette.
const MEDALS: Array<{ disc: number; ring: number }> = [
  { disc: 0xffd23f, ring: 0xa9760a },
  { disc: 0xd6dee6, ring: 0x8593a3 },
  { disc: 0xd9904f, ring: 0x8a5026 },
];

// How many pixels below the header the list starts.
const LIST_TOP = 150;
// Gap between the bottom of the scroll viewport and the BACK button.
const BACK_BTN_MARGIN = 12;
// Height of the BACK button.
const BACK_BTN_H = 56;
// How far the BACK button sits above the bottom edge (before safe-area).
const BACK_BTN_OFFSET = 56;

interface LeaderboardData {
  returnTo?: string;
}

export class LeaderboardScene extends Phaser.Scene {
  static readonly KEY = 'LeaderboardScene';
  private returnTo = 'MainMenuScene';
  private loader?: Phaser.GameObjects.Container;

  // Scroll state
  private scrollContainer?: Phaser.GameObjects.Container;
  private scrollMin = 0; // minimum scrollY (negative = scrolled down)
  private scrollMax = 0; // always 0 (top)
  private dragStartY = 0;
  private dragStartScroll = 0;
  private isDragging = false;
  private fadeTop?: Phaser.GameObjects.Graphics;
  private fadeBottom?: Phaser.GameObjects.Graphics;
  private viewportTop = 0;
  private viewportH = 0;

  constructor() {
    super({ key: LeaderboardScene.KEY });
  }

  create(data?: LeaderboardData): void {
    this.returnTo = data?.returnTo ?? 'MainMenuScene';
    const { width, height } = this.scale;
    const cx = width / 2;
    const pal = activePalette();

    // Reset scroll state on each create() call.
    this.scrollMin = 0;
    this.scrollMax = 0;
    this.isDragging = false;
    this.scrollContainer = undefined;
    this.fadeTop = undefined;
    this.fadeBottom = undefined;

    // Neon-candy backdrop.
    addNeonBackdrop(this, { glowY: height * 0.28, bokeh: true });

    // Brand + title + accent underline.
    this.add
      .text(cx, 56, 'SHMERGE', {
        fontFamily: FONT, fontSize: '13px', fontStyle: 'bold', color: css(pal.primary), resolution: DPR,
      })
      .setOrigin(0.5)
      .setAlpha(0.7);
    this.add
      .text(cx, 92, 'Leaderboard', {
        fontFamily: FONT, fontSize: '32px', fontStyle: 'bold', color: css(pal.text), resolution: DPR,
      })
      .setOrigin(0.5);
    const ul = this.add.graphics();
    ul.fillStyle(pal.secondary, 0.9);
    ul.fillRoundedRect(cx - 24, 114, 48, 3, 1.5);

    // ── BACK button ──────────────────────────────────────────────────────────
    let bottomInset = 0;
    try {
      const v = getComputedStyle(document.documentElement).getPropertyValue('--sab').trim();
      const n = parseFloat(v);
      if (Number.isFinite(n) && n >= 0) bottomInset = n;
    } catch {}
    const backBtnY = height - BACK_BTN_OFFSET - bottomInset;

    createButton(this, {
      x: cx, y: backBtnY, width: 220, height: BACK_BTN_H, label: 'BACK', fontSize: 18,
      depth: 10,
      onClick: () => {
        resumeAudio();
        this.scene.start(this.returnTo);
      },
    });

    // ── Viewport geometry ────────────────────────────────────────────────────
    // Tablet: if CSS pixel width >= 700, use a wider/taller panel.
    const isTablet = width >= 700;

    // The viewport sits between LIST_TOP and just above the BACK button.
    const vpTop = LIST_TOP;
    const vpBottom = backBtnY - BACK_BTN_H / 2 - BACK_BTN_MARGIN;
    const vpH = vpBottom - vpTop;

    this.viewportTop = vpTop;
    this.viewportH = vpH;

    // Card width: wider on tablets.
    const cardW = isTablet ? Math.min(width - 48, 600) : Math.min(width - 28, 372);

    this.showLoader(cx, vpTop + 70);

    void fetchTop(20)
      .then((entries: LeaderboardEntry[]) => {
        if (!this.scene.isActive()) return;
        this.loader?.destroy();
        this.loader = undefined;
        this.renderEntries(cx, cardW, vpTop, vpH, entries);
      })
      .catch(() => {
        if (!this.scene.isActive()) return;
        this.loader?.destroy();
        this.loader = undefined;
        this.renderMessage(cx, vpTop + 40, 'Leaderboard unavailable', 'Check your connection and try again.', pal.textMuted);
      });
  }

  /** A spinning accent arc — friendlier than static "Loading…" text. */
  private showLoader(cx: number, cy: number): void {
    const pal = activePalette();
    const c = this.add.container(cx, cy);
    const ring = this.add.graphics();
    ring.lineStyle(4, pal.surfaceBorder, 1);
    ring.strokeCircle(0, 0, 18);
    ring.lineStyle(4, pal.primary, 1);
    ring.beginPath();
    ring.arc(0, 0, 18, -Math.PI / 2, Math.PI * 0.35, false);
    ring.strokePath();
    const label = this.add
      .text(0, 38, 'Loading scores…', {
        fontFamily: FONT, fontSize: '13px', color: css(pal.textMuted), resolution: DPR,
      })
      .setOrigin(0.5);
    c.add([ring, label]);
    this.tweens.add({ targets: ring, angle: 360, duration: 900, repeat: -1, ease: 'Linear' });
    this.loader = c;
  }

  /** Centered icon + headline + sub-line. Used for empty + error states. */
  private renderMessage(cx: number, cy: number, head: string, sub: string, accent: number): void {
    const pal = activePalette();
    const g = this.add.graphics();
    drawTrophy(g, cx, cy, accent, 2.4);
    g.setAlpha(0.5);
    this.add
      .text(cx, cy + 56, head, {
        fontFamily: FONT, fontSize: '18px', fontStyle: 'bold', color: css(pal.text), resolution: DPR,
      })
      .setOrigin(0.5);
    this.add
      .text(cx, cy + 82, sub, {
        fontFamily: FONT, fontSize: '13px', color: css(pal.textMuted), resolution: DPR,
        align: 'center', wordWrap: { width: 280 },
      })
      .setOrigin(0.5);
  }

  private renderEntries(
    cx: number,
    cardW: number,
    vpTop: number,
    vpH: number,
    entries: LeaderboardEntry[],
  ): void {
    const pal = activePalette();
    if (entries.length === 0) {
      this.renderMessage(cx, vpTop + 60, 'No scores yet', 'Be the first to make the board!', pal.secondary);
      return;
    }

    const cardX = cx - cardW / 2;
    const padX = 18;
    const headerH = 34;
    const rowH = 27;
    const listH = headerH + entries.length * rowH + 12;

    // The scroll container holds the card + all rows. Its y is the scroll offset.
    const sc = this.add.container(0, vpTop);
    this.scrollContainer = sc;

    // ── Card background (inside scroll container, y is relative to vpTop) ───
    const card = this.add.graphics().setDepth(-0.4);
    card.fillStyle(pal.chipShadow, 0.4);
    card.fillRoundedRect(cardX, 4, cardW, listH, 16);
    card.fillStyle(pal.surface, 0.88);
    card.fillRoundedRect(cardX, 0, cardW, listH, 16);
    card.lineStyle(1, pal.surfaceBorder, 1);
    card.strokeRoundedRect(cardX, 0, cardW, listH, 16);
    card.fillStyle(0xffffff, 0.04);
    card.fillRoundedRect(cardX + 5, 4, cardW - 10, headerH, 13);
    sc.add(card);

    // ── Column header ────────────────────────────────────────────────────────
    sc.add(
      this.add
        .text(cardX + padX + 34, headerH / 2 + 3, 'PLAYER', {
          fontFamily: FONT, fontSize: '11px', fontStyle: 'bold', color: css(pal.textLabel), resolution: DPR,
        })
        .setOrigin(0, 0.5),
    );
    sc.add(
      this.add
        .text(cardX + cardW - padX, headerH / 2 + 3, 'SCORE', {
          fontFamily: FONT, fontSize: '11px', fontStyle: 'bold', color: css(pal.textLabel), resolution: DPR,
        })
        .setOrigin(1, 0.5),
    );
    const div = this.add.graphics();
    div.fillStyle(pal.surfaceBorder, 0.7);
    div.fillRect(cardX + padX, headerH + 2, cardW - padX * 2, 1);
    sc.add(div);

    // ── Rows ─────────────────────────────────────────────────────────────────
    const playerName = getPlayerName();
    let playerRowIdx = -1;

    const firstRowY = headerH + 8 + rowH / 2;
    entries.forEach((e, i) => {
      const rowY = firstRowY + i * rowH;
      const isTop = i < 3;
      const isPlayer = playerName !== '' && e.playerName === playerName;
      if (isPlayer && playerRowIdx === -1) playerRowIdx = i;

      const rowC = this.add.container(0, 0);

      // Highlight band.
      const band = this.add.graphics();
      if (isPlayer) {
        // Soft accent highlight for the current player's row.
        band.fillStyle(pal.primary, 0.18);
        band.fillRoundedRect(cardX + 8, rowY - rowH / 2 + 1, cardW - 16, rowH - 2, 7);
      } else if (i === 0) {
        band.fillStyle(pal.coin, 0.1);
        band.fillRoundedRect(cardX + 8, rowY - rowH / 2 + 1, cardW - 16, rowH - 2, 7);
      } else if (i < 3) {
        band.fillStyle(0xffffff, 0.05);
        band.fillRoundedRect(cardX + 8, rowY - rowH / 2 + 1, cardW - 16, rowH - 2, 7);
      } else if (i % 2 === 1) {
        band.fillStyle(0xffffff, 0.025);
        band.fillRoundedRect(cardX + 8, rowY - rowH / 2 + 1, cardW - 16, rowH - 2, 7);
      }
      rowC.add(band);

      // Rank badge.
      const badgeX = cardX + padX + 8;
      if (isTop) {
        const m = MEDALS[i];
        const badge = this.add.graphics();
        drawMedal(badge, badgeX, rowY, 9.5, m.disc, m.ring);
        rowC.add(badge);
        rowC.add(
          this.add
            .text(badgeX, rowY - 0.5, `${i + 1}`, {
              fontFamily: FONT, fontSize: '11px', fontStyle: 'bold', color: '#3a2a08', resolution: DPR,
            })
            .setOrigin(0.5),
        );
      } else {
        rowC.add(
          this.add
            .text(badgeX, rowY, `${i + 1}`, {
              fontFamily: FONT, fontSize: '13px', fontStyle: 'bold',
              color: css(isPlayer ? pal.primary : pal.textMuted), resolution: DPR,
            })
            .setOrigin(0.5),
        );
      }

      // Name — bold + accent if this is the player.
      rowC.add(
        this.add
          .text(cardX + padX + 34, rowY, e.playerName, {
            fontFamily: FONT,
            fontSize: '14px',
            fontStyle: isTop || isPlayer ? 'bold' : 'normal',
            color: css(isPlayer ? lighten(pal.primary, 0.2) : isTop ? pal.text : pal.textSoft),
            resolution: DPR,
          })
          .setOrigin(0, 0.5),
      );

      // Score.
      rowC.add(
        this.add
          .text(cardX + cardW - padX, rowY, e.score.toLocaleString('en-US'), {
            fontFamily: FONT, fontSize: '14px', fontStyle: 'bold',
            color: css(i === 0 ? pal.coin : pal.text), resolution: DPR,
          })
          .setOrigin(1, 0.5),
      );

      // Gentle staggered entrance.
      rowC.setAlpha(0).setX(-10);
      this.tweens.add({
        targets: rowC, alpha: 1, x: 0,
        duration: 240, delay: Math.min(i, 12) * 28, ease: 'Quad.easeOut',
      });

      sc.add(rowC);
    });

    // ── Scrollable mask ───────────────────────────────────────────────────────
    // Phaser GeometryMask: mask shape is in *world* coordinates. The shape must
    // NOT be on the display list (make, not add) or it renders as a white rect.
    const maskShape = this.make.graphics(undefined, false);
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(cardX, vpTop, cardW, vpH);
    const mask = maskShape.createGeometryMask();
    sc.setMask(mask);

    // Calculate scroll bounds.
    // scrollY (sc.y) ranges from vpTop (top of list) down to vpTop - overflow.
    const overflow = Math.max(0, listH - vpH);
    this.scrollMin = -overflow; // most negative = fully scrolled to bottom
    this.scrollMax = 0;         // 0 offset = top of list

    // ── Auto-scroll to player row ─────────────────────────────────────────────
    if (playerRowIdx >= 0 && overflow > 0) {
      // Row center in list-local Y coords:
      const rowCenterInList = firstRowY + playerRowIdx * rowH;
      // We want rowCenterInList + sc.y - vpTop == vpH/2
      //  => sc.y = vpTop + vpH/2 - rowCenterInList
      const targetScrollY = vpH / 2 - rowCenterInList;
      const clampedScroll = Math.max(this.scrollMin, Math.min(this.scrollMax, targetScrollY));
      sc.y = vpTop + clampedScroll;
    }
    // If overflow === 0, all entries fit; no scrolling needed.

    // ── Fade overlays (edge cues) ──────────────────────────────────────────────
    this.fadeTop = this.add.graphics().setDepth(5);
    this.fadeBottom = this.add.graphics().setDepth(5);
    this.updateFades(cardX, cardW, vpTop, vpH);

    // ── Scroll: wheel ─────────────────────────────────────────────────────────
    if (overflow > 0) {
      this.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gos: unknown, _dx: number, dy: number) => {
        this.applyScrollDelta(-dy * 1.2, cardX, cardW, vpTop, vpH);
      });

      // ── Scroll: touch/mouse drag ────────────────────────────────────────────
      this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
        // Only initiate drag if pointer is inside the viewport.
        if (p.y >= vpTop && p.y <= vpTop + vpH) {
          this.isDragging = true;
          this.dragStartY = p.y;
          this.dragStartScroll = sc.y - vpTop;
        }
      });

      this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
        if (!this.isDragging || !p.isDown) return;
        const delta = p.y - this.dragStartY;
        const newScroll = Math.max(this.scrollMin, Math.min(this.scrollMax, this.dragStartScroll + delta));
        sc.y = vpTop + newScroll;
        this.updateFades(cardX, cardW, vpTop, vpH);
      });

      this.input.on('pointerup', () => { this.isDragging = false; });
      this.input.on('pointerupoutside', () => { this.isDragging = false; });
    }
  }

  private applyScrollDelta(
    delta: number,
    cardX: number,
    cardW: number,
    vpTop: number,
    vpH: number,
  ): void {
    if (!this.scrollContainer) return;
    const sc = this.scrollContainer;
    const currentScroll = sc.y - vpTop;
    const newScroll = Math.max(this.scrollMin, Math.min(this.scrollMax, currentScroll + delta));
    sc.y = vpTop + newScroll;
    this.updateFades(cardX, cardW, vpTop, vpH);
  }

  private updateFades(cardX: number, cardW: number, vpTop: number, vpH: number): void {
    if (!this.fadeTop || !this.fadeBottom || !this.scrollContainer) return;
    const sc = this.scrollContainer;
    const currentScroll = sc.y - vpTop; // 0 = at top, negative = scrolled down
    const fadeH = 28;

    this.fadeTop.clear();
    this.fadeBottom.clear();

    const pal = activePalette();
    const bgColor = pal.bgBottom;

    // Top fade: visible when we're scrolled down (currentScroll < 0).
    if (currentScroll < -2) {
      this.fadeTop.fillGradientStyle(bgColor, bgColor, bgColor, bgColor, 0.7, 0.7, 0, 0);
      this.fadeTop.fillRect(cardX, vpTop, cardW, fadeH);
    }

    // Bottom fade: visible when there's more content below.
    const overflow = -this.scrollMin;
    if (overflow > 0 && currentScroll > this.scrollMin + 2) {
      this.fadeBottom.fillGradientStyle(bgColor, bgColor, bgColor, bgColor, 0, 0, 0.7, 0.7);
      this.fadeBottom.fillRect(cardX, vpTop + vpH - fadeH, cardW, fadeH);
    }
  }
}
